import { useGetPolicyAsset } from '@/utils/useGetPolicyAsset';
import {
  Badge,
  Blockquote,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
  StepperFooter,
  cn,
  formatPrice,
  getResolver,
  useAssertedParams,
  useStepperContext,
} from '@korint/folio';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  firstPaymentEnum,
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  getComponent,
  invoiceFrequencyEnum,
  invoiceTimingEnum,
  policyControllerCalculatePolicyQuote,
  policyControllerEditPolicy,
  policyControllerGetPolicyQueryOptions,
  policyControllerQuotePolicy,
  type CustomFieldDto,
} from 'korint-sdk';
import { sortBy } from 'lodash';
import { AlertCircleIcon, CheckIcon, StarIcon } from 'lucide-react';
import { useEffect, useState, type Dispatch } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const availableTiers = {
  THREE_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 3 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
      </div>
    ),
    warrantyInformation: (luxe: boolean) => (
      <div className="px-4 py-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <div>
            <span className="text-lg font-semibold">+ de 120</span>
            <br />
            pièces couvertes
          </div>

          <div>
            <span className="text-lg">
              Jusqu'à{' '}
              <span className="font-semibold">
                {formatPrice(luxe ? 750000 : 300000)}
                <sup>*</sup>
              </span>
              <br />
              par intervention
            </span>
          </div>
        </div>
      </div>
    ),
    doesItHaveLuxeOption: true,
  },
  FOUR_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 4 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
      </div>
    ),
    warrantyInformation: (_luxe: boolean) => (
      <div className="px-4 py-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <div>
            <span className="text-lg font-semibold">+ de 120</span>
            <br />
            pièces couvertes
          </div>

          <div>
            <span className="text-lg">
              Jusqu'à{' '}
              <span className="font-semibold">
                {formatPrice(1000000)}
                <sup>*</sup>
              </span>
              <br />
              par intervention
            </span>
          </div>
        </div>
      </div>
    ),
    doesItHaveLuxeOption: true,
  },
  FIVE_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
      </div>
    ),
    warrantyInformation: (luxe: boolean) => (
      <div className="px-4 py-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <div>
            <span className="text-lg font-semibold">
              + de 300
              <br />
            </span>
            pièces couvertes
          </div>

          <div>
            {luxe ? (
              <span className="text-lg">
                Jusqu'à{' '}
                <span className="font-semibold">
                  {formatPrice(1500000)}
                  <sup>*</sup>
                </span>
                <br />
                par intervention
              </span>
            ) : (
              <span className="text-lg">
                Plafond{' '}
                <span className="font-semibold">
                  VRADE<sup>*</sup>
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    ),
    doesItHaveLuxeOption: true,
  },
  FIVE_STARS_ECO: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex items-center">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
          <span className="text-xs ml-1 self-center">éco</span>
        </div>
      </div>
    ),
    warrantyInformation: (_luxe?: boolean) => (
      <div className="px-4 py-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <div>
            <span className="text-lg font-semibold">
              + de 300
              <br />
            </span>
            pièces couvertes
          </div>

          <div>
            <span className="text-lg">
              Jusqu'à{' '}
              <span className="font-semibold">
                {formatPrice(1000000)}
                <sup>*</sup>
              </span>
              <br />
              par intervention
            </span>
          </div>
        </div>
      </div>
    ),
    doesItHaveLuxeOption: false,
  },
};

const tiersOrder = ['FIVE_STARS_ECO', 'FIVE_STARS', 'FOUR_STARS', 'THREE_STARS'];
type Tier = 'FIVE_STARS_ECO' | 'FIVE_STARS' | 'FOUR_STARS' | 'THREE_STARS';

function PolicyFunnelTier({
  tier,
  isSelected,
  setSelectedTier,
  policyDuration,
  accessBasicPlans,
  isHigherTierAvailable,
}: {
  tier: {
    tier: string;
    perils: string[];
    excess: string;
  };
  isSelected: boolean;
  setSelectedTier: (tier: string) => void;
  policyDuration: number;
  accessBasicPlans: boolean;
  isHigherTierAvailable?: boolean;
}) {
  const { policyId, branchId } = useAssertedParams();
  const { data: vehicle } = useGetPolicyAsset();

  const { data, status } = useQuery({
    queryKey: ['calculate-quote', policyDuration, tier.tier, vehicle?.id, accessBasicPlans],
    enabled: !!vehicle,
    queryFn: async () => {
      try {
        const res = await policyControllerCalculatePolicyQuote(
          policyId,
          {
            assetIds: [vehicle.id],
            options: [
              {
                assetId: vehicle.id,
                tier: tier.tier,
                excess: tier.excess,
              },
            ],
            customFieldsToOverride: [{ key: 'accessBasicPlans', value: accessBasicPlans }],
            periodDurationOverride: {
              amount: policyDuration,
              unit: 'MONTH',
            },
          },
          {
            branchId,
          },
        );

        return res;
      } catch (e) {
        const isErrorNormalItsJustTheApiThatIsShitty =
          (e as { errorResponse?: { data: { code: string } } })?.errorResponse?.data?.code ===
          'QUOTE_FAILED';

        if (isErrorNormalItsJustTheApiThatIsShitty) {
          return null;
        }

        throw e;
      }
    },
    retry: 0,
  });

  const policyTotal = getComponent(data?.policy, 'CLAIM_MANAGEMENT')?.premium
    .amountWithFeesAndTaxes;
  const assetTotal = data?.assets?.[0]?.totalPremium.amountWithFeesAndTaxes;
  const isLuxe = data?.assets?.[0]?.priceBand?.includes('LUXE') ?? false;
  let totalPrice: number | undefined;
  if (policyTotal && assetTotal) {
    totalPrice = policyTotal + assetTotal;
  }

  return (
    <button
      type="button"
      className={cn(
        'relative w-full text-left border-2 rounded-xl p-6 transition-all duration-200 flex flex-col justify-between cursor-pointer',
        {
          'cursor-not-allowed opacity-60 border-gray-300 bg-gray-50 hover:shadow-lg':
            status === 'pending' || status === 'error' || !totalPrice,
          'border-primary bg-primary/5 shadow-md':
            isSelected && !(status === 'pending' || status === 'error' || !totalPrice),
          'border-gray-200 hover:border-gray-300':
            !isSelected && !(status === 'pending' || status === 'error' || !totalPrice),
        },
      )}
      onClick={() => setSelectedTier(tier.tier)}
      disabled={status === 'pending' || status === 'error' || !totalPrice}
    >
      {isHigherTierAvailable && (
        <div className="absolute -top-2 -left-2 bg-primary text-white rounded-full py-1 px-2 text-xs">
          Recommandée par Garantie Plus
        </div>
      )}
      {isSelected && (
        <div className="absolute -top-2 -right-2 bg-primary text-white rounded-full p-1">
          <CheckIcon className="h-4 w-4" />
        </div>
      )}
      <div>
        {isLuxe && availableTiers[tier.tier as keyof typeof availableTiers].doesItHaveLuxeOption ? (
          <Badge variant="gold">Luxe</Badge>
        ) : null}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Label htmlFor={tier.tier} className="sr-only">
              {tier.tier}
            </Label>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {availableTiers[tier.tier as keyof typeof availableTiers]?.name}
            </h3>
            {availableTiers[tier.tier as keyof typeof availableTiers]?.warrantyInformation(isLuxe)}
          </div>

          <div className="space-y-2">
            {tier.perils.map(peril => (
              <div key={peril} className="flex items-start space-x-2">
                <CheckIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700 leading-relaxed">{peril}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-gray-500">* Voir conditions générales.</p>
      </div>
      <div className="flex self-end mt-4">
        {status === 'pending' ? (
          <Skeleton className="w-24 h-4" />
        ) : status === 'success' && totalPrice ? (
          <div className="text-lg font-semibold self-end">{formatPrice(totalPrice)}</div>
        ) : (
          <div className="text-muted-foreground italic self-end">Indisponible</div>
        )}
      </div>
    </button>
  );
}

function useGetTiersQuotes({
  tiers,
  accessBasicPlans,
}: {
  tiers: Tier[];
  accessBasicPlans: boolean;
}) {
  const { policyId, branchId } = useAssertedParams();
  const { data: vehicle } = useGetPolicyAsset();

  const allTiersQueries = useQueries({
    queries: tiers.map(tier => ({
      queryKey: ['calculate-quote-all-tiers', tier, vehicle?.id, accessBasicPlans],
      enabled: !!vehicle,
      queryFn: async () => {
        try {
          const res = await policyControllerCalculatePolicyQuote(
            policyId,
            {
              assetIds: [vehicle.id],
              options: [
                {
                  assetId: vehicle.id,
                  tier,
                  excess: 'DEFAULT',
                },
              ],
              customFieldsToOverride: [{ key: 'accessBasicPlans', value: accessBasicPlans }],
            },
            {
              branchId,
            },
          );
          return { status: 'success', data: res };
        } catch (e) {
          const isQuoteFailedError =
            (e as { errorResponse?: { data: { code: string } } })?.errorResponse?.data?.code ===
            'QUOTE_FAILED';

          if (isQuoteFailedError) {
            const quoteErrors =
              (
                e as {
                  errorResponse?: {
                    data: {
                      details: {
                        quoteErrors: ({ key: string; error: string } & Record<string, string>)[];
                      };
                    };
                  };
                }
              )?.errorResponse?.data?.details.quoteErrors ?? [];

            return { status: 'error', errors: quoteErrors };
          }
        }
      },
      retry: 0,
    })),
    combine: results => ({
      isLoading: results.some(result => result.isPending),
      data: results.map(result => result.data),
    }),
  });

  return allTiersQueries;
}

function useIsTierAvailable({
  tier,
  accessBasicPlans,
}: {
  tier: 'FIVE_STARS_ECO' | 'FIVE_STARS' | 'FOUR_STARS' | 'THREE_STARS';
  accessBasicPlans: boolean;
}) {
  const { policyId, branchId } = useAssertedParams();
  const { data: vehicle } = useGetPolicyAsset();

  const { data: isTierAvailable } = useQuery({
    queryKey: ['calculate-quote', tier, vehicle?.id, accessBasicPlans],
    enabled: !!vehicle,
    queryFn: async () => {
      try {
        await policyControllerCalculatePolicyQuote(
          policyId,
          {
            assetIds: [vehicle.id],
            options: [
              {
                assetId: vehicle.id,
                tier,
                excess: 'DEFAULT',
              },
            ],
            customFieldsToOverride: [{ key: 'accessBasicPlans', value: accessBasicPlans }],
          },
          {
            branchId,
          },
        );

        return true;
      } catch (e) {
        const cannotQuoteAsset =
          (e as { errorResponse?: { data: { code: string } } })?.errorResponse?.data?.code ===
          'QUOTE_FAILED';

        if (cannotQuoteAsset) {
          return false;
        }

        throw e;
      }
    },
    retry: 0,
  });

  return isTierAvailable;
}

type QuoteError = { key: string; error: string } & Record<string, string>;

/**
 * Groups pricing errors to keep only the widest criterion
 * for each refusal type (age, mileage, price, energy, etc.)
 */
function groupQuoteErrorsByWidestCriterion(errors: QuoteError[]): QuoteError[] {
  const errorsByKey = new Map<string, QuoteError[]>();

  // Group errors by key (criterion type)
  for (const error of errors) {
    const existing = errorsByKey.get(error.key) ?? [];
    existing.push(error);
    errorsByKey.set(error.key, existing);
  }

  const groupedErrors: QuoteError[] = [];

  for (const [, keyErrors] of errorsByKey.entries()) {
    if (keyErrors.length === 0) continue;

    // For errors with min/max limits, keep the widest limit
    const hasMax = keyErrors.some(e => e.max !== undefined);
    const hasMin = keyErrors.some(e => e.min !== undefined);

    if (hasMax) {
      // For max values, keep the highest max (most permissive)
      const widestError = keyErrors.reduce((prev, curr) => {
        const prevMax = prev.max ? Number.parseFloat(prev.max) : Number.NEGATIVE_INFINITY;
        const currMax = curr.max ? Number.parseFloat(curr.max) : Number.NEGATIVE_INFINITY;
        return currMax > prevMax ? curr : prev;
      }, keyErrors[0]);
      groupedErrors.push(widestError);
    } else if (hasMin) {
      // For min values, keep the lowest min (most permissive)
      const widestError = keyErrors.reduce((prev, curr) => {
        const prevMin = prev.min ? Number.parseFloat(prev.min) : Number.POSITIVE_INFINITY;
        const currMin = curr.min ? Number.parseFloat(curr.min) : Number.POSITIVE_INFINITY;
        return currMin < prevMin ? curr : prev;
      }, keyErrors[0]);
      groupedErrors.push(widestError);
    } else {
      groupedErrors.push(keyErrors[0]);
    }
  }

  return groupedErrors;
}

/**
 * Formats grouped errors into readable messages for the user
 */
function formatGroupedErrorsForUser(
  errors: QuoteError[],
  vehicleAge: number,
  vehicleMileage: number | undefined,
  vehiclePrice: number | undefined,
  vehicleEnergy: string | undefined,
): string[] {
  const messages: string[] = [];

  for (const error of errors) {
    const { key, max, min } = error;

    if (key === 'age' && max) {
      const maxAge = Number.parseInt(max, 10);
      messages.push(
        `Votre véhicule a ${vehicleAge} ans, mais il ne doit pas avoir plus de ${maxAge} ans.`,
      );
    } else if (key === 'mileage' && max) {
      const maxMileage = Number.parseInt(max, 10);
      const formattedMileage = maxMileage.toLocaleString('fr-FR');
      messages.push(
        `Votre véhicule a ${vehicleMileage?.toLocaleString('fr-FR') ?? 'N/A'} km, mais il ne doit pas avoir plus de ${formattedMileage} km.`,
      );
    } else if (key === 'price' && max) {
      const maxPrice = Number.parseInt(max, 10);
      messages.push(
        `Votre véhicule vaut ${vehiclePrice?.toLocaleString('fr-FR') ?? 'N/A'} €, mais il ne doit pas valoir plus de ${maxPrice.toLocaleString('fr-FR')} €.`,
      );
    } else if (key === 'price' && min) {
      const minPrice = Number.parseInt(min, 10);
      messages.push(
        `Votre véhicule vaut ${vehiclePrice?.toLocaleString('fr-FR') ?? 'N/A'} €, mais il doit valoir au moins ${minPrice.toLocaleString('fr-FR')} €.`,
      );
    } else if (key === 'energy') {
      messages.push(
        `Le type d'énergie de votre véhicule (${vehicleEnergy ?? 'N/A'}) n'est pas éligible.`,
      );
    } else if (key === 'brand') {
      messages.push(`Votre véhicule n'est éligible à aucune formule.`);
    }
  }

  return messages;
}

function useAvailableTiers({ accessBasicPlans }: { accessBasicPlans: boolean }) {
  const tiersMap = {
    FIVE_STARS_ECO: useIsTierAvailable({ tier: 'FIVE_STARS_ECO', accessBasicPlans }),
    FIVE_STARS: useIsTierAvailable({ tier: 'FIVE_STARS', accessBasicPlans }),
    FOUR_STARS: useIsTierAvailable({ tier: 'FOUR_STARS', accessBasicPlans }),
    THREE_STARS: useIsTierAvailable({ tier: 'THREE_STARS', accessBasicPlans }),
  };

  return {
    isLoading: Object.values(tiersMap).some(v => v === undefined),
    availableTiers: Object.entries(tiersMap)
      .filter(([, available]) => available)
      .map(([tier]) => tier),
  };
}

function ErrorBlock({
  accessBasicPlans,
  allTiers,
  setActivateStepper,
}: Readonly<{
  accessBasicPlans: boolean;
  allTiers: Tier[];
  setActivateStepper: Dispatch<React.SetStateAction<boolean>>;
}>) {
  const { data: vehicle } = useGetPolicyAsset();

  const { data: responses } = useGetTiersQuotes({
    tiers: allTiers,
    accessBasicPlans,
  });

  const noQuoteAvailable = responses.every(response => response?.status === 'error');

  const vehicleAge = vehicle
    ? dayjs().diff(dayjs(vehicle.customFields.vehicleFirstRegistrationDate), 'year')
    : 0;
  const vehicleMileage = vehicle?.customFields?.vehicleMileage;
  const vehiclePrice = vehicle?.customFields?.latestPriceInEuro;
  const vehicleEnergy = vehicle?.customFields?.energy;

  // Collect all errors from all tiers that failed
  const allErrors: QuoteError[] = [];
  for (const response of responses) {
    if (response?.status === 'error' && response.errors) {
      allErrors.push(...response.errors);
    }
  }

  // Group errors to keep only the widest criterion
  // for each refusal type (age, mileage, price, energy, etc.)
  const groupedErrors = groupQuoteErrorsByWidestCriterion(allErrors);

  // Format grouped errors into readable messages
  const errorMessages =
    groupedErrors.length > 0
      ? formatGroupedErrorsForUser(
          groupedErrors,
          vehicleAge,
          vehicleMileage,
          vehiclePrice,
          vehicleEnergy,
        )
      : [];

  if (noQuoteAvailable) {
    setActivateStepper(false);
    return (
      <Blockquote className="mb-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="flex-shrink-0">
              <AlertCircleIcon className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Votre véhicule n'est éligible à aucune formule.
            </h3>
            <div className="mt-2 text-sm text-red-700 space-y-1">
              {errorMessages.map(message => (
                <p key={message}>{message}</p>
              ))}
              {!accessBasicPlans && (
                <p className="mt-2 italic">
                  Vous pouvez essayer de faire une demande de dérogation ci-dessous.
                </p>
              )}
            </div>
          </div>
        </div>
      </Blockquote>
    );
  }
  setActivateStepper(true);

  return null;
}

const policyDurationOptions: Array<{ value: string; label: string }> = [
  { value: '6', label: '6 mois' },
  { value: '12', label: '12 mois' },
  { value: '24', label: '24 mois' },
];

function PolicyDurationSelector({
  value,
  onValueChange,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Durée de la garantie</CardTitle>
        <CardDescription>Sélectionnez la durée de votre garantie</CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup value={value} onValueChange={onValueChange} className="flex gap-4">
          {options.map(({ value: optionValue, label }) => (
            <div key={optionValue} className="flex items-center gap-2">
              <RadioGroupItem value={optionValue} id={`policyDuration-${optionValue}`} />
              <Label htmlFor={`policyDuration-${optionValue}`}>{label}</Label>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}

export function PolicyFunnelQuote() {
  const { t } = useTranslation();

  const { policyId, branchId } = useAssertedParams();
  const queryClient = useQueryClient();
  const { data: config } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions('garantiePlusAssistance'),
  });
  const { data: vehicle, isLoading: isAssetLoading } = useGetPolicyAsset();
  const stepper = useStepperContext();

  const [activateStepper, setActivateStepper] = useState(true);

  const { data: currentPolicyDuration } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, {
      branchId,
    }),
    select: policy => policy?.periodDuration.amount,
  });

  const { data: productId } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, {
      branchId,
    }),
    select: policy => policy?.productId,
  });

  const form = useForm<{
    selectedTier: string;
    accessBasicPlans: string;
    policyDuration: string;
  }>({
    defaultValues: {
      selectedTier: '',
      accessBasicPlans: 'false',
      policyDuration: currentPolicyDuration?.toString() || '12',
    },
    resolver: getResolver([
      {
        name: 'selectedTier',
        type: 'select',
        required: true,
        options: tiersOrder.map(tier => ({
          value: tier,
          label: tier,
        })),
      },
    ]),
  });

  const selectedTier = useWatch({ control: form.control, name: 'selectedTier' });
  const accessBasicPlans = useWatch({ control: form.control, name: 'accessBasicPlans' });
  const policyDuration = Number(useWatch({ control: form.control, name: 'policyDuration' }));

  const { mutateAsync: editAndQuotePolicy, isPending } = useMutation({
    mutationFn: async () => {
      // Use outer-scope useWatch values — `selectedTier`, `policyDuration`, and
      // `accessBasicPlans` are all available from the closure.
      // Using the input values is wrong because the form getResolver did not include the arguments we were trying to unpack

      const invoicingConfig = {
        frequency: invoiceFrequencyEnum.YEARLY,
        timing: invoiceTimingEnum.IN_ADVANCE,
        firstPayment: firstPaymentEnum.FULL_AT_SIGNATURE,
      };

      const res = await policyControllerEditPolicy(
        policyId,
        {
          invoicingConfig,
          customFields: [
            { key: 'accessBasicPlans', value: accessBasicPlans === 'true' },
          ] satisfies CustomFieldDto[],
          ...(policyDuration
            ? { periodDuration: { amount: Number(policyDuration), unit: 'MONTH' } }
            : {}),
        },
        {
          branchId,
        },
        { client: clientWithFork },
      );

      await policyControllerQuotePolicy(
        policyId,
        {
          options: [
            {
              assetId: vehicle.id,
              tier: selectedTier,
              excess: 'DEFAULT',
            },
          ],
        },
        {
          branchId: res.newBranchId ?? branchId,
        },
      );

      if (res.newBranchId) {
        stepper.next({
          branchId: res.newBranchId,
        });
      } else {
        stepper.next();
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries();
    },
  });

  const { availableTiers, isLoading } = useAvailableTiers({
    accessBasicPlans: accessBasicPlans === 'true',
  });

  // Default to the highest available tier (the first one in our case, since we sorted them accordingly)
  useEffect(() => {
    if (isLoading || isAssetLoading) return;

    if (!selectedTier || !availableTiers.includes(selectedTier)) {
      if (vehicle) {
        form.setValue('selectedTier', vehicle.quote?.options.tier ?? availableTiers[0]);
      } else {
        form.setValue('selectedTier', availableTiers[0]);
      }
    }
  }, [selectedTier, availableTiers, isLoading, vehicle, isAssetLoading]);

  const tiers = sortBy(
    config.quote!.availableConfigs.filter(({ tier }) => availableTiers.includes(tier)),
    ({ tier }) => tiersOrder.indexOf(tier),
  );

  const tiersWithPerils = tiers.map(tier => ({
    ...tier,
    perils: tier.perils?.map(peril => t([`${productId}.perils.${peril.id}.label`])) ?? [],
  }));

  return (
    <div className="flex flex-col gap-4">
      <PolicyDurationSelector
        value={policyDuration.toString()}
        onValueChange={async value => {
          form.setValue('policyDuration', value);
        }}
        options={policyDurationOptions}
      />

      {isLoading ? (
        <Skeleton className="h-200 w-full" />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Choisissez votre formule</CardTitle>
            <CardDescription>
              Sélectionnez le niveau de protection que vous souhaitez
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isLoading && (
              <ErrorBlock
                allTiers={config.quote!.availableConfigs.map<Tier>(({ tier }) => tier as Tier)}
                accessBasicPlans={accessBasicPlans === 'true'}
                setActivateStepper={setActivateStepper}
              />
            )}
            <div className="flex flex-col gap-4">
              <RadioGroup
                value={selectedTier}
                onValueChange={value => form.setValue('selectedTier', value)}
              >
                <div
                  className={cn(
                    'grid gap-6',
                    // Tailwind needs to be explicit about the classnames, it cannot be dynamic
                    // https://tailwindcss.com/docs/detecting-classes-in-source-files
                    tiers.length === 1 && 'lg:mx-auto lg:w-2/5',
                    tiers.length === 2 && 'lg:grid-cols-2',
                    tiers.length === 3 && 'lg:grid-cols-3',
                    tiers.length === 4 && 'lg:grid-cols-4',
                    tiers.length > 4 && 'lg:grid-cols-3',
                  )}
                >
                  {tiersWithPerils.map((tier, index) => (
                    <PolicyFunnelTier
                      key={tier.tier}
                      tier={tier}
                      isSelected={selectedTier === tier.tier}
                      setSelectedTier={value => form.setValue('selectedTier', value)}
                      accessBasicPlans={accessBasicPlans === 'true'}
                      policyDuration={policyDuration}
                      isHigherTierAvailable={index === 0}
                    />
                  ))}
                </div>
              </RadioGroup>
              <div className="flex flex-col gap-2">
                <Label>Souhaitez-vous effectuer une demande de dérogation ?</Label>
                <RadioGroup
                  value={accessBasicPlans}
                  onValueChange={value => form.setValue('accessBasicPlans', value)}
                  className="flex gap-2"
                >
                  <div className="flex items-center gap-2 mr-4">
                    <RadioGroupItem value="true" id="accessBasicPlans-true" />
                    <Label htmlFor="accessBasicPlans-true">Oui</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="false" id="accessBasicPlans-false" />
                    <Label htmlFor="accessBasicPlans-false">Non</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <StepperFooter
        nextButtonEnabled={activateStepper}
        loading={isPending}
        onNext={form.handleSubmit(async () => {
          editAndQuotePolicy();
        })}
      />
    </div>
  );
}
